import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Globe, Loader2, MessageCircle, Package, ShoppingCart, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toWhatsAppNumber } from "@/components/notes/NotifyCustomerDialog";
import { StatusBadge, STATUS } from "@/shop/ShopAccount";
import { formatZAR } from "@/shop/ShopContext";
import { useAuth } from "@/context/AuthContext";
import { OnlineShopSettings } from "@/components/shop/OnlineShopSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shop/admin`;
const TABS = ["PLACED", "PACKING", "READY", "COLLECTED", "CANCELLED"];

const readyMessage = (o) =>
  `Hi ${(o.customer_name || "").split(" ")[0] || "there"}, your Bosveld online order ${o.id} is packed and ready for collection. Total ${formatZAR(o.total)} — pay when you collect. See you soon!`;

const OrderRow = ({ o, onStatus, onTill }) => (
  <Card data-testid={`online-order-${o.id}`}>
    <CardContent className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-bold">{o.id} <span className="ml-2"><StatusBadge s={o.status} /></span></p>
          <p className="text-sm text-slate-600">{o.customer_name} · {o.phone} · {o.customer_email}</p>
          <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("en-ZA")}{o.discount_percent > 0 && ` · account discount ${o.discount_percent}%`}{o.sale_number && ` · paid on ${o.sale_number}`}</p>
          {o.notes && <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">Note: {o.notes}</p>}
        </div>
        <p className="text-xl font-extrabold" data-testid={`online-order-total-${o.id}`}>{formatZAR(o.total)}</p>
      </div>
      <ul className="mt-3 divide-y rounded-lg border text-sm">
        {o.items.map((i) => (
          <li key={i.product_id} className="flex justify-between gap-2 px-3 py-1.5">
            <span><b>{i.quantity}</b> × {i.name} <span className="text-xs text-slate-400">({i.plu_code})</span></span>
            <span>{formatZAR(i.line_total)}</span>
          </li>
        ))}
      </ul>
      {["PLACED", "PACKING", "READY"].includes(o.status) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {o.status === "PLACED" && <Button size="sm" variant="outline" onClick={() => onStatus(o, "PACKING")} data-testid={`order-pack-${o.id}`}><Package className="mr-1 h-4 w-4" /> Start packing</Button>}
          {o.status !== "READY" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onStatus(o, "READY")} data-testid={`order-ready-${o.id}`}><MessageCircle className="mr-1 h-4 w-4" /> Ready + WhatsApp</Button>}
          {o.status === "READY" && (
            <Button size="sm" variant="outline" onClick={() => window.open(`https://wa.me/${toWhatsAppNumber(o.phone)}?text=${encodeURIComponent(readyMessage(o))}`, "_blank", "noopener")} data-testid={`order-whatsapp-${o.id}`}>
              <MessageCircle className="mr-1 h-4 w-4" /> WhatsApp again
            </Button>
          )}
          <Button size="sm" className="bg-[#D96B27] hover:bg-[#C2591B]" onClick={() => onTill(o)} data-testid={`order-till-${o.id}`}><ShoppingCart className="mr-1 h-4 w-4" /> Take payment at till</Button>
          <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => onStatus(o, "CANCELLED")} data-testid={`order-cancel-${o.id}`}><XCircle className="mr-1 h-4 w-4" /> Cancel</Button>
        </div>
      )}
    </CardContent>
  </Card>
);

export default function OnlineOrders() {
  const [tab, setTab] = useState("PLACED");
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const load = useCallback(() => axios.get(`${API}/orders`, { params: { status: tab } }).then(({ data: d }) => setData(d)).catch(() => toast.error("Could not load online orders")), [tab]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const setStatus = async (o, status) => {
    if (status === "CANCELLED" && !window.confirm(`Cancel ${o.id}? The customer should be told.`)) return;
    try {
      await axios.put(`${API}/orders/${o.id}/status`, { status });
      if (status === "READY") window.open(`https://wa.me/${toWhatsAppNumber(o.phone)}?text=${encodeURIComponent(readyMessage(o))}`, "_blank", "noopener");
      toast.success(`${o.id}: ${STATUS[status][0]}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update order");
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="online-orders-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-3xl font-bold"><Globe className="h-7 w-7 text-[#D96B27]" /> Online Orders</h1>
        <a href="/shop" target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#D96B27] hover:underline" data-testid="open-shop-link">Open the shop ↗</a>
      </div>
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <Button key={t} variant={tab === t ? "default" : "outline"} onClick={() => setTab(t)} data-testid={`online-orders-tab-${t}`}>
            {STATUS[t][0]} {data?.counts?.[t] ? `(${data.counts[t]})` : ""}
          </Button>
        ))}
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin" /> : data.orders.length === 0 ? (
        <p className="py-10 text-center text-slate-500" data-testid="online-orders-empty">No orders here</p>
      ) : (
        <div className="space-y-3">{data.orders.map((o) => <OrderRow key={o.id} o={o} onStatus={setStatus} onTill={(x) => navigate("/sales", { state: { loadOnlineOrderId: x.id } })} />)}</div>
      )}
      {isAdmin && <OnlineShopSettings />}
    </div>
  );
}
